import type { StaffLogin } from '../interfaces/login.interfaces'
import { LOGIN } from '~/constants/routes/routes.constants'
import { API_ME } from '~/constants/routes/api/auth/routes.api.constants'

export default defineNuxtPlugin((nuxtApp) => {
  // Skip plugin when rendering error page
  if (nuxtApp.payload.error) {
    return {}
  }

  const authReady = useState('authReady', () => false)
  const session = useState<StaffLogin | null>('authSession', () => null)
  const updateSessionFn: Ref<(() => Promise<void>) | null> = ref(null)

  const loggedIn = computed(() => !!session.value?.email)
  const isImpersonateUser: Ref<boolean> = computed(() => !!session.value?.original_user)
  const isAdmin: Ref<boolean> = computed(() => session.value?.role ? session.value.role === 'ROLE_SUPER_ADMIN' || session.value.role === 'ROLE_ADMIN' : false)
  const isSuperAdmin: Ref<boolean> = computed(() => session.value?.role ? session.value.role === 'ROLE_SUPER_ADMIN' : false)

  const redirectTo = useState('authRedirect')

  if (import.meta.client) {
    const config = useRuntimeConfig().public

    const refreshSession = async () => {
      try {
        session.value = await $fetch<StaffLogin>(API_ME.path, {
          baseURL: config.api_base_url,
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
      } catch {
        session.value = null
      }
    }

    refreshSession().finally(() => {
      updateSessionFn.value = refreshSession
      authReady.value = true
    })

    const currentRoute = useRoute()

    addRouteMiddleware(
      'auth',
      (to) => {
        if (!authReady.value) {
          return
        }

        if (to.meta.auth && !loggedIn.value) {
          redirectTo.value = to.path
          return LOGIN.path
        } else if (updateSessionFn.value) {
          updateSessionFn.value()
        }
      },
      { global: true }
    )

    watch(authReady, (ready) => {
      if (ready) {
        if (updateSessionFn.value) {
          setInterval(() => {
            updateSessionFn.value?.()
          }, 120 * 1000)
        }

        if (!loggedIn.value && currentRoute.meta.auth) {
          redirectTo.value = currentRoute.path
          navigateTo(LOGIN.path)
        }
      }
    }, { once: true })

    watch(loggedIn, (isLoggedIn) => {
      if (!isLoggedIn && currentRoute.meta.auth && authReady.value) {
        redirectTo.value = currentRoute.path
        reloadNuxtApp({
          path: LOGIN.path,
          ttl: 1000
        })
      }
    })
  }

  return {
    provide: {
      auth: {
        session,
        updateSession: updateSessionFn,
        loggedIn,
        isImpersonateUser,
        isAdmin,
        isSuperAdmin,
        authReady
      }
    }
  }
})
